// AppShell.tsx — the device-agnostic app frame.
//
// Fills the viewport (dynamic vh so mobile chrome doesn't clip content), paints
// the warm canvas, hosts the scrollable screen and the bottom tab bar, and
// reserves room for safe-area insets. Overlays (step viewer, arrive, regulate)
// render as children above this, full-bleed.
import React from 'react';
import { TabBar, type TabId } from './TabBar';
import { Header } from './Header';
import { SideNav } from './SideNav';
import { useIsDesktop } from '@/hooks/useIsDesktop';

export function AppShell({
  tab,
  onTab,
  onOpenSettings,
  onOpenProgress,
  showProgress = true,
  onInspire,
  onOpenHelp,
  showFrame = true,
  screenKey,
  scrollResetKey,
  children,
  overlays,
  tabsLocked = false,
  onTabLocked,
}: {
  /** TRIAL_FULL_LOCK: every tab but Today greyed with a lock; tapping calls onTabLocked, no tab switch. */
  tabsLocked?: boolean;
  onTabLocked?: () => void;
  tab: TabId;
  onTab: (id: TabId) => void;
  onOpenSettings: () => void;
  onOpenProgress: () => void;
  /** TODAY_ONE_JOB: hide the header "Your recovery" (chart) icon until there is
      progress worth showing (day-one it opens an empty room). Native-gated by the
      caller; defaults to visible so web is untouched. */
  showProgress?: boolean;
  onInspire: () => void;
  onOpenHelp: () => void;
  /** Hide the header + tab bar (e.g. while a full-screen overlay is shown). */
  showFrame?: boolean;
  /** Changing this key replays the gentle screen fade. */
  screenKey: string;
  /** Drives the scroll-to-top reset. Excludes the programme track, so pressing the
      track toggle doesn't jump the page to the top. Falls back to screenKey. */
  scrollResetKey?: string;
  children: React.ReactNode;
  overlays?: React.ReactNode;
}) {
  const isDesktop = useIsDesktop();

  // Every screen opens at the TOP. The single scroll surface persists across tab /
  // module changes, so without this the previous screen's scroll position carries
  // over (e.g. coming out of consent left Today scrolled down). Reset on screenKey.
  const scrollAreaRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const el = scrollAreaRef.current;
    if (!el) return;
    el.scrollTo(0, 0);
    requestAnimationFrame(() => el.scrollTo(0, 0));
  }, [scrollResetKey ?? screenKey]);

  // Web desktop shell: a left nav rail + a comfortably-wide centred reading
  // column on the ambient backdrop. Phones never hit the breakpoint, so this
  // branch is web-only and leaves the native app untouched.
  if (isDesktop) {
    return (
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          overflow: 'hidden',
          color: 'var(--ink)',
        }}
      >
        {showFrame && <SideNav tab={tab} onTab={onTab} locked={tabsLocked} onLocked={onTabLocked} onOpenSettings={onOpenSettings} onOpenProgress={onOpenProgress} onInspire={onInspire} onOpenHelp={onOpenHelp} />}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <div
            key={scrollResetKey ?? screenKey}
            ref={scrollAreaRef}
            data-scroll-area
            style={{
              position: 'absolute',
              inset: 0,
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            <div
              key={screenKey}
              style={{
                maxWidth: 760,
                margin: '0 auto',
                minHeight: '100%',
                background: 'var(--cream)',
                borderLeft: '1px solid var(--hair)',
                borderRight: '1px solid var(--hair)',
                boxShadow: '0 0 90px -28px rgba(74,52,24,0.22)',
                animation: 'fade .4s ease',
              }}
            >
              {children}
            </div>
          </div>
          {/* Overlays (step viewer, exercise viewer, celebrations…) are constrained
              to the same centred column width as the feed, so images and content
              don't stretch full-window on desktop. The frame itself must NOT capture
              pointer events (or it would block the screen underneath when no overlay
              is open) — only the actual overlay children do. */}
          <style>{`.rn-overlay-frame{pointer-events:none}.rn-overlay-frame>*{pointer-events:auto}`}</style>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
            {/* id="rn-overlay-root" — the portal target for overlays that need to be
                mounted deep in a screen's tree but rendered here, at the width-
                constrained column, alongside the inline overlays above (e.g. Calm Your
                Body's full-screen steps — see CalmYourBodyPanel.tsx's FullScreenShell). */}
            <div id="rn-overlay-root" className="rn-overlay-frame" style={{ position: 'relative', width: '100%', maxWidth: 760 }}>
              {overlays}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      // id="rn-overlay-root" — see the desktop branch above: the same portal target
      // for overlays mounted deep in a screen's tree, here the naturally-constrained
      // (maxWidth 520) phone column.
      id="rn-overlay-root"
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: 520,
        margin: '0 auto',
        background: 'var(--cream)',
        overflow: 'hidden',
        color: 'var(--ink)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* The header and tab bar are LOCKED: they sit OUTSIDE the scroller as flex
          children (flexShrink:0), so they physically cannot move on scroll or
          overscroll. ONLY the middle area scrolls. (The previous single-scroller-with-
          sticky-bars drifted on the iOS WebView — the whole frame lifted when the feed
          reached the bottom and rubber-banded.) */}
      {showFrame && (
        <Header
          onOpenProgress={onOpenProgress}
          showProgress={showProgress}
          onOpenSettings={onOpenSettings}
          onInspire={onInspire}
          onOpenHelp={onOpenHelp}
        />
      )}
      <div
        ref={scrollAreaRef}
        data-scroll-area
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div key={screenKey} style={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column', animation: 'fade .4s ease' }}>
          {children}
        </div>
      </div>
      {showFrame && <TabBar tab={tab} onTab={onTab} locked={tabsLocked} onLocked={onTabLocked} />}

      {overlays}
    </div>
  );
}
